/**
 * @module AdaptersPage
 * @package @geenius/adapters/react
 * @description Renders the Tailwind-based adapters overview for the React
 * package. The page summarizes adapter health, supports status filtering, and
 * provides an accessible modal workflow for configuring each adapter domain.
 */

"use client";

import type {
  AdapterDomain,
  AdapterStatusType,
  DomainAdapterConfig,
} from "@geenius/adapters";
import {
  ADAPTER_DOMAINS,
  ADAPTER_STATUSES,
  DOMAIN_LABELS,
  getDomainConfig,
  getProvidersForDomain,
  isAdaptersConfigured,
} from "@geenius/adapters";
import type { JSX } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { AdapterConfigForm } from "../components/AdapterConfigForm";
import { AdapterList } from "../components/AdapterList";
import { AdapterStatusBadge } from "../components/AdapterStatusBadge";
import { useAdapters } from "../hooks";
import { cn } from "../lib/utils";
import { PageErrorBoundary } from "./PageErrorBoundary";

const DIALOG_FOCUSABLE_SELECTORS =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type RegistryEntry = {
  domain: AdapterDomain;
  provider: string;
  status: AdapterStatusType;
};

/**
 * Props accepted by the React adapters overview page.
 *
 * @property onNavigateDetail Optional callback used instead of opening the inline modal
 * @property onConfigure Optional callback invoked with submitted adapter configuration
 * @property className Optional class name merged onto the page root
 * @property configByDomain Optional configuration snapshot used to hydrate modal forms
 * @property registryEntries Optional adapter registry snapshot rendered above the grid
 */
export interface AdaptersPageProps {
  onNavigateDetail?: (domain: AdapterDomain) => void;
  onConfigure?: (
    domain: AdapterDomain,
    config: DomainAdapterConfig,
  ) => Promise<void> | void;
  className?: string;
  configByDomain?: Partial<Record<AdapterDomain, DomainAdapterConfig>>;
  registryEntries?: RegistryEntry[];
}

/**
 * Returns focusable descendants for an active dialog container.
 *
 * @param container Dialog container used to scope the focus trap
 * @returns Interactive descendants that should participate in tab order
 */
function getDialogFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(DIALOG_FOCUSABLE_SELECTORS),
  );
}

/**
 * Renders the adapter management dashboard for the React variant.
 *
 * @param props Page props controlling navigation overrides and registry data
 * @returns A dashboard with status filters, adapter cards, and a modal editor
 * @example
 * ```tsx
 * <AdaptersPage onNavigateDetail={(domain) => navigate(`/adapters/${domain}`)} />
 * ```
 */
export function AdaptersPage(props: AdaptersPageProps): JSX.Element {
  return (
    <PageErrorBoundary
      className={props.className}
      title="Adapters failed to load"
    >
      <AdaptersPageContent {...props} />
    </PageErrorBoundary>
  );
}

function AdaptersPageContent({
  onNavigateDetail,
  onConfigure,
  className,
  configByDomain,
  registryEntries,
}: AdaptersPageProps): JSX.Element {
  const { statuses, isLoading } = useAdapters();
  const [selectedDomain, setSelectedDomain] = useState<AdapterDomain | null>(
    null,
  );
  const [filterStatus, setFilterStatus] = useState<
    AdapterStatusType | undefined
  >();
  const modalRef = useRef<HTMLDivElement | null>(null);
  const modalTitleId = useId();
  const adapterDomainsHeadingId = useId();
  const initialConfigFor = (domain: AdapterDomain) =>
    configByDomain?.[domain] ??
    (isAdaptersConfigured() ? getDomainConfig(domain) : undefined) ??
    undefined;

  const counts = {
    connected: ADAPTER_DOMAINS.filter(
      (domain) => statuses[domain]?.status === "connected",
    ).length,
    error: ADAPTER_DOMAINS.filter(
      (domain) => statuses[domain]?.status === "error",
    ).length,
    total: ADAPTER_DOMAINS.length,
  };

  useEffect(() => {
    if (!selectedDomain) {
      return;
    }

    const browserDocument = typeof document === "undefined" ? null : document;
    if (!browserDocument) {
      return;
    }

    const dialog = modalRef.current;
    if (!dialog) {
      return;
    }

    const previousActiveElement =
      browserDocument.activeElement instanceof HTMLElement
        ? browserDocument.activeElement
        : null;
    const focusableElements = getDialogFocusableElements(dialog);
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements.at(-1);

    (firstFocusableElement ?? dialog).focus();

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        setSelectedDomain(null);
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const activeElement = browserDocument.activeElement;

      if (!firstFocusableElement || !lastFocusableElement) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      if (event.shiftKey && activeElement === firstFocusableElement) {
        event.preventDefault();
        lastFocusableElement.focus();
        return;
      }

      if (!event.shiftKey && activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    };

    browserDocument.addEventListener("keydown", handleKeyDown);

    return () => {
      browserDocument.removeEventListener("keydown", handleKeyDown);
      previousActiveElement?.focus();
    };
  }, [selectedDomain]);

  if (isLoading) {
    return (
      <div className={cn("mx-auto max-w-4xl space-y-6", className)}>
        <div
          aria-label="Loading adapters"
          aria-live="polite"
          className="animate-pulse space-y-4 motion-reduce:animate-none"
          role="status"
        >
          <div className="h-8 w-48 rounded bg-muted" />
          <div className="h-4 w-72 rounded bg-muted/70" />
          <div className="grid gap-3 sm:grid-cols-3">
            {[1, 2, 3].map((index) => (
              <div
                key={index}
                className="h-20 rounded-lg border border-border bg-card"
              />
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {[1, 2, 3, 4].map((index) => (
              <div
                key={index}
                className="h-28 rounded-lg border border-border bg-card"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("mx-auto max-w-4xl space-y-8 text-foreground", className)}
    >
      <header className="space-y-1">
        <h1 className="text-2xl font-extrabold text-foreground">Adapters</h1>
        <p className="text-sm text-muted-foreground">
          Manage your infrastructure adapters - database, auth, AI, storage,
          payments, and admin.
        </p>
      </header>

      <section className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-muted-foreground">
            Total
          </p>
          <p className="mt-1 text-2xl font-extrabold text-foreground">
            {counts.total}
          </p>
          <p className="text-[0.625rem] text-muted-foreground">
            adapter domains
          </p>
        </div>
        <div className="rounded-lg border border-primary/20 bg-primary/10 p-4 shadow-sm">
          <p className="text-xs font-semibold uppercase text-primary">
            Connected
          </p>
          <p className="mt-1 text-2xl font-extrabold text-primary">
            {counts.connected}
          </p>
          <p className="text-[0.625rem] text-primary/70">active adapters</p>
        </div>
        {counts.error > 0 && (
          <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase text-destructive">
              Errors
            </p>
            <p className="mt-1 text-2xl font-extrabold text-destructive">
              {counts.error}
            </p>
            <p className="text-[0.625rem] text-destructive/70">
              need attention
            </p>
          </div>
        )}
      </section>

      {registryEntries && registryEntries.length > 0 && (
        <section className="rounded-lg border border-border bg-card p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">
                Adapter Registry
              </p>
              <p className="text-sm text-muted-foreground">
                Live adapter registry entries.
              </p>
            </div>
            <div className="flex gap-3 text-xs text-muted-foreground">
              <span>{registryEntries.length} entries</span>
            </div>
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {registryEntries.map((entry) => {
              const providerMeta = getProvidersForDomain(entry.domain).find(
                (provider) => provider.id === entry.provider,
              );

              return (
                <div
                  key={`${entry.domain}-${entry.provider}`}
                  className="rounded-md border border-border bg-background px-4 py-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {DOMAIN_LABELS[entry.domain]}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {providerMeta?.name ?? entry.provider}
                      </p>
                    </div>
                    <AdapterStatusBadge status={entry.status} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setFilterStatus(undefined)}
          aria-pressed={!filterStatus}
          className={cn(
            "rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors active:border-primary/30 active:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none",
            !filterStatus
              ? "border-primary bg-primary/10 text-primary"
              : "border-border bg-background text-muted-foreground hover:text-foreground",
          )}
        >
          All
        </button>
        {ADAPTER_STATUSES.map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setFilterStatus(status)}
            aria-pressed={filterStatus === status}
            className={cn(
              "flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors active:border-primary/30 active:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none",
              filterStatus === status
                ? "border-primary bg-primary/10 text-primary"
                : "border-border bg-background text-muted-foreground hover:text-foreground",
            )}
          >
            <AdapterStatusBadge status={status} />
          </button>
        ))}
      </section>

      <section aria-labelledby={adapterDomainsHeadingId}>
        <h2 id={adapterDomainsHeadingId} className="sr-only">
          Adapter domains
        </h2>
        <AdapterList
          filterStatus={filterStatus}
          onSelect={(domain) => {
            if (onNavigateDetail) {
              onNavigateDetail(domain);
            } else {
              setSelectedDomain(domain);
            }
          }}
        />
      </section>

      {selectedDomain && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 px-4 backdrop-blur-sm">
          <button
            type="button"
            aria-label="Close adapter configuration"
            className="absolute inset-0 cursor-default border-0 bg-transparent p-0"
            onClick={() => setSelectedDomain(null)}
          />
          <div
            ref={modalRef}
            className="relative z-10 w-full max-w-lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby={modalTitleId}
            tabIndex={-1}
          >
            <AdapterConfigForm
              domain={selectedDomain}
              titleId={modalTitleId}
              initialConfig={initialConfigFor(selectedDomain)}
              onSave={async (config) => {
                await onConfigure?.(selectedDomain, config);
                setSelectedDomain(null);
              }}
              onCancel={() => setSelectedDomain(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
